import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { groupsService, plansService } from "services";
import { shiftType } from "services/shiftsService";
import { isAdmin, isSupervisor } from "utils/utils";
import Loading from "components/Loading";
import MobilePlanDayList from "./MobilePlanDayList";
import MobilePlanGroupPicker from "./MobilePlanGroupPicker";
import { planReopenLog } from "utils/planWorkflowAbsences";

const getWeekRange = (refDate) => {
	const start = moment(refDate).startOf("isoWeek");
	const end = moment(refDate).endOf("isoWeek");
	return {
		start: start.format("YYYY-MM-DD"),
		end: end.format("YYYY-MM-DD"),
	};
};

const getWeekLabel = (refDate, t) => {
	const now = moment();
	const startOfWeek = moment(refDate).startOf("isoWeek");

	if (now.isSame(startOfWeek, "isoWeek")) return t("plan.mobile.thisWeek");

	const diffWeeks = startOfWeek.diff(now.startOf("isoWeek"), "weeks");
	if (diffWeeks === 1) return t("plan.mobile.nextWeek");
	if (diffWeeks === -1) return t("plan.mobile.lastWeek");

	const start = moment(refDate).startOf("isoWeek");
	const end = moment(refDate).endOf("isoWeek");
	return `${start.format("D MMM")} - ${end.format("D MMM")}`;
};

const getWeekDays = (refDate) => {
	const start = moment(refDate).startOf("isoWeek");
	const days = [];
	for (let i = 0; i < 7; i++) {
		days.push(start.clone().add(i, "days").format("YYYY-MM-DD"));
	}
	return days;
};

const checkEmployeeActive = (ranges, day) => {
	if (!ranges || ranges.length === 0) return true;
	const dayMoment = moment(day, "YYYY-MM-DD");
	return ranges.some((r) => {
		const start = moment(r.start, "YYYY-MM-DD");
		const end = r.end ? moment(r.end, "YYYY-MM-DD") : null;
		return dayMoment.isSameOrAfter(start) && (!end || dayMoment.isSameOrBefore(end));
	});
};

const processAssignments = (data) => {
	if (!data) return [];
	return data.map((item) => {
		const ranges = item.ranges || [];
		const plans = (item.plans || []).map((plan) => ({
			...plan,
			active: checkEmployeeActive(ranges, plan.day),
		}));
		return { ...item, plans };
	});
};

const normalizePlanGroup = (g) => {
	if (!g || g.id == null) return null;
	return {
		id: g.id,
		name: g.name || "",
		total_employees:
			g.total_employees ??
			g.employees_count ??
			g.employees?.length ??
			null,
	};
};

const VIEW_PERSON = "person";
const VIEW_GROUP = "group";

const MobilePlanView = ({ routeGroupId }) => {
	const { t } = useTranslation();
	const user = useSelector((state) => state.authReducer?.user);

	useEffect(() => {
		planReopenLog(
			"MobilePlanView montado (ancho ≤767px). Reabrir ausencia solo existe en Plan.js escritorio.",
			{ routeGroupId }
		);
	}, [routeGroupId]);

	const allowGroupVisualization = useMemo(() => {
		return (
			user?.employee_with_contract?.contract
				?.allow_group_planning_visualization || false
		);
	}, [user]);
	const userIsAdmin = isAdmin();
	const userIsSupervisor = isSupervisor();
	const canSwitchViewMode =
		allowGroupVisualization || userIsAdmin || userIsSupervisor;

	const canPickAnotherGroup = userIsAdmin || userIsSupervisor;

	const homeGroupId = user?.employee_with_group?.group?.id ?? null;

	const [currentWeek, setCurrentWeek] = useState(moment());
	const [viewMode, setViewMode] = useState(
		allowGroupVisualization ? VIEW_PERSON : VIEW_GROUP
	);

	const [selectedGroupId, setSelectedGroupId] = useState(undefined);
	const [selectableGroupsRaw, setSelectableGroupsRaw] = useState([]);
	const [groupsListLoading, setGroupsListLoading] = useState(false);

	useEffect(() => {
		setSelectedGroupId(undefined);
	}, [routeGroupId]);

	const effectiveGroupId = useMemo(() => {
		if (selectedGroupId != null) return selectedGroupId;
		if (routeGroupId) return routeGroupId;
		if (homeGroupId != null) return homeGroupId;
		if (user?.groups_supervised?.length > 0)
			return user.groups_supervised[0].id;
		return undefined;
	}, [
		selectedGroupId,
		routeGroupId,
		homeGroupId,
		user?.groups_supervised,
	]);

	useEffect(() => {
		if (!canPickAnotherGroup || viewMode !== VIEW_GROUP) {
			return undefined;
		}

		let cancelled = false;

		const load = async () => {
			if (userIsAdmin) {
				setGroupsListLoading(true);
				try {
					const res = await groupsService.getAll({
						sort: "+name",
						variant: "detailed",
					});
					if (!cancelled && res.ok) {
						setSelectableGroupsRaw(Array.isArray(res.data) ? res.data : []);
					} else if (!cancelled) {
						setSelectableGroupsRaw([]);
					}
				} catch (e) {
					if (!cancelled) setSelectableGroupsRaw([]);
				} finally {
					if (!cancelled) setGroupsListLoading(false);
				}
			} else {
				setSelectableGroupsRaw(user?.groups_supervised || []);
				setGroupsListLoading(false);
			}
		};

		load();
		return () => {
			cancelled = true;
		};
	}, [canPickAnotherGroup, userIsAdmin, user?.groups_supervised, viewMode]);

	const orderedSelectableGroups = useMemo(() => {
		const list = (selectableGroupsRaw || [])
			.map(normalizePlanGroup)
			.filter(Boolean);
		const byId = new Map(list.map((g) => [g.id, g]));
		const unique = Array.from(byId.values());

		const restSorted = (arr) =>
			[...arr].sort((a, b) =>
				a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
			);

		if (homeGroupId == null) return restSorted(unique);

		const home = unique.find((g) => g.id === homeGroupId);
		const rest = unique.filter((g) => g.id !== homeGroupId);
		return home ? [home, ...restSorted(rest)] : restSorted(unique);
	}, [selectableGroupsRaw, homeGroupId]);

	const [allAssignments, setAllAssignments] = useState([]);
	const [loading, setLoading] = useState(true);

	const showGroupPicker =
		canPickAnotherGroup &&
		viewMode === VIEW_GROUP &&
		!groupsListLoading &&
		orderedSelectableGroups.length > 0;

	const planAssignments = useMemo(() => {
		if (viewMode === VIEW_GROUP || !user?.id) return allAssignments;
		return allAssignments.filter((a) => a.employee?.id === user.id);
	}, [allAssignments, viewMode, user?.id]);

	const weekRange = useMemo(() => getWeekRange(currentWeek), [currentWeek]);
	const weekDays = useMemo(() => getWeekDays(currentWeek), [currentWeek]);
	const weekLabel = useMemo(() => getWeekLabel(currentWeek, t), [currentWeek, t]);

	const fetchAssignments = useCallback(async () => {
		if (!effectiveGroupId) {
			setLoading(false);
			return;
		}

		setLoading(true);

		try {
			const options = {
				group_id: effectiveGroupId,
				start: weekRange.start,
				end: weekRange.end,
				shift_type: shiftType.work,
			};

			const response = await plansService.getPlanAssignments(options);

			if (response.ok) {
				setAllAssignments(processAssignments(response.data));
			} else {
				setAllAssignments([]);
			}
		} catch (error) {
			console.error("Error fetching plan assignments:", error);
			setAllAssignments([]);
		} finally {
			setLoading(false);
		}
	}, [effectiveGroupId, weekRange.start, weekRange.end]);

	useEffect(() => {
		fetchAssignments();
	}, [fetchAssignments]);

	const handlePrevWeek = () => {
		setCurrentWeek((prev) => moment(prev).subtract(1, "week"));
	};

	const handleNextWeek = () => {
		setCurrentWeek((prev) => moment(prev).add(1, "week"));
	};

	const hasDayData = weekDays.some((day) =>
		planAssignments.some((a) => {
			const dayPlan = a.plans?.find((p) => p.day === day);
			return (
				dayPlan &&
				dayPlan.active &&
				dayPlan.assignments?.some(
					(s) =>
						s.id !== -1 &&
						s.shift &&
						s.shift.type === "Trabajo"
				)
			);
		})
	);

	return (
		<div className="mobile-plan-view">
			<div className="mobile-plan-view__header">
				<h2 className="mobile-plan-view__title">{t("plan.mobile.title")}</h2>

				{canSwitchViewMode && (
					<div className="mobile-plan-view__switch">
						<button
							className={`mobile-plan-view__switch-btn ${viewMode === VIEW_PERSON ? "mobile-plan-view__switch-btn--active" : ""}`}
							onClick={() => setViewMode(VIEW_PERSON)}
						>
							{t("plan.mobile.myShifts")}
						</button>
						<button
							className={`mobile-plan-view__switch-btn ${viewMode === VIEW_GROUP ? "mobile-plan-view__switch-btn--active" : ""}`}
							onClick={() => setViewMode(VIEW_GROUP)}
						>
							{t("common.group")}
						</button>
					</div>
				)}

				{showGroupPicker && (
					<div className="mobile-plan-view__group-slot">
						<MobilePlanGroupPicker
							groups={orderedSelectableGroups}
							selectedGroupId={effectiveGroupId}
							homeGroupId={homeGroupId}
							onSelectGroup={setSelectedGroupId}
							allowSwitch={orderedSelectableGroups.length > 1}
						/>
					</div>
				)}

				<div className="mobile-plan-view__week-nav">
					<button
						className="mobile-plan-view__week-arrow"
						onClick={handlePrevWeek}
					>
						<FiChevronLeft size={22} />
					</button>
					<span className="mobile-plan-view__week-label">{weekLabel}</span>
					<button
						className="mobile-plan-view__week-arrow"
						onClick={handleNextWeek}
					>
						<FiChevronRight size={22} />
					</button>
				</div>
			</div>

			<div className="mobile-plan-view__body">
				{loading ? (
					<div className="mobile-plan-view__loading">
						<Loading />
					</div>
				) : !hasDayData ? (
					<div className="mobile-plan-view__empty">
						{t("plan.mobile.noShifts")}
					</div>
				) : (
					weekDays.map((day) => (
					<MobilePlanDayList
						key={day}
						dayData={day}
						planAssignments={planAssignments}
						showAvatar={viewMode === VIEW_GROUP}
					/>
					))
				)}
			</div>
		</div>
	);
};

export default MobilePlanView;
